





















































































































import {
  Vue,
  Prop,
  Component,
} from "@/shared/vuepack";
import {
  RiskToleranceQuestionnaireForm,
  RiskToleranceQuestionnaireAnswer,
  RiskToleranceQuestionnaireField,
  RiskToleranceType,
  DrivewealthWizardStep
} from "@/shared/types";
import { Validator } from "vee-validate";
import * as Routes from "@/employee/route-names";
import * as SharedRoutes from "@/shared/route-names";
import * as drivewealth from "@/shared/custodianSettings/drivewealthTypes";
import WizardNavigation from "@/employee/components/investments/drivewealth/wizard/WizardNavigation.vue";
import CuratedRtqInstumentsTable from "@/employee/components/investments/drivewealth/CuratedRtqInstumentsTable.vue";
import GenericLoadingSpinner from "@/shared/components/ui/GenericLoadingSpinner.vue";
import { useDrivewealthWizardStore } from "@/shared/store/drivewealthWizard";
import { useRiskToleranceQuestionnaireStore } from "@/shared/store/riskToleranceQuestionnaire";
import { useDrivewealthSweepStore } from "@/shared/store/drivewealthSweep";
import { useThemeStore } from "@/shared/store/theme";

@Component({
  components: {
    WizardNavigation,
    GenericLoadingSpinner,
    CuratedRtqInstumentsTable
  }
})
export default class DrivewealthCuratedRtq extends Vue {
  $validator: Validator;
  Routes = Routes;
  RiskToleranceQuestionnaireAnswer = RiskToleranceQuestionnaireAnswer;

  @Prop()
  isWizard: boolean;
  @Prop()
  accountId: number;

  @Prop({required: false})
  isPreenrollment: boolean;

  @Prop({required: false})
  isCreatingAccount: boolean;

  @Prop({required: false})
  isSavingAccountClaims: boolean;

  drivewealthWizardStore = useDrivewealthWizardStore();
  riskToleranceQuestionnaireStore = useRiskToleranceQuestionnaireStore();
  questionaireFactory = drivewealth.RtqQuestionaireFactory();
  drivewealthSweepStore = useDrivewealthSweepStore();
  themeStore = useThemeStore();
  riskQuestionnaireQuestions = drivewealth.RiskToleranceQuestionnaireFields;

  createAccountPosting = false;
  formModel: RiskToleranceQuestionnaireForm;

  mounted (): void {
    this.drivewealthWizardStore.loadDividendReinvestmentDisclosures({});
    this.riskToleranceQuestionnaireStore.getRiskToleranceQuestionnaireResults();
    this.drivewealthSweepStore.getSweepV2({});
  }

  scrollToTop (): void {
    window.scrollTo({
      behavior: "smooth",
      left: 0,
      top: 0
    });
  }

  submitForm (): void {
    const questionsCount: number = this.riskQuestionnaireQuestions.length;
    const userResponses: RiskToleranceQuestionnaireAnswer[] = this.riskQuestionnaireQuestions.map(x => {
      if (x.userResponse) {
        return x.userResponse;
      }
    });

    if (questionsCount !== userResponses.length) {
      return;
    }

    this.formModel = {
      userResponses,
      accountId: this.accountId
    };

    this.$validator
      .validate()
      .then(isValid => {
        if (isValid) {
          let saveToDb = true;
          if (!this.isWizard) {
            saveToDb = false;
          }
          this.riskToleranceQuestionnaireStore.storeRiskToleranceQuestionnaireResults(this.formModel, saveToDb);
          this.scrollToTop();
        }
      });
  }

  retakeQuiz (): void {
    this.riskToleranceQuestionnaireStore.clearStoredResults();
    this.scrollToTop();
  }

  acceptQuizResult (): void {
    if (this.isWizard) {
      this.tryAdvance();
    } else {
      this.riskToleranceQuestionnaireStore.storeRiskToleranceQuestionnaireResults(this.formModel, true)
          .then(() => {
            if (this.accountId && !this.isWizard) {
              this.$router.replace({
                name: SharedRoutes.DrivewealthInvestmentAccountV2,
                params: {accountId: `${this.accountId}`},
              });
            } else if (this.drivewealthSweepStore.sweepIsActive) {
              this.$router.replace({name: Routes.DrivewealthSweepV2});
            } else {
              this.$router.replace({name: Routes.InvestmentAccountInfo});
            }
          });
    }
  }

  get createAction () {
    if (this.isCreatingAccount) {
      return this.drivewealthWizardStore.createCustomerAccount;
    }
    if (this.isPreenrollment) {
      return this.drivewealthWizardStore.preenrollCustomer;
    }
    if (this.isSavingAccountClaims) {
      return this.drivewealthWizardStore.claimAccount;
    }

    return () => Promise.reject();
  }

  tryAdvance (): void {
    if (!this.hasSavedRiskToleranceType) {
      return;
    }

    this.createAccountPosting = true;
    this.createAction(this.accountId)
    .then(() => {
      this.drivewealthWizardStore.setWizardStepComplete(this.wizardStep);
    })
    .finally(() => {
      this.createAccountPosting = false;
    });
  }

  get isPosting (): boolean {
    return this.riskToleranceQuestionnaireStore.isPosting;
  }

  get isLoaded (): boolean {
    return this.riskToleranceQuestionnaireStore.isLoaded;
  }

  get hasSavedRiskToleranceType (): boolean {
    return this.riskToleranceQuestionnaireStore.riskToleranceType !== RiskToleranceType.None;
  }

  get riskToleranceTypeDescription (): string {
    let description = "";
    switch (this.riskToleranceQuestionnaireStore.riskToleranceType) {
      case RiskToleranceType.Conservative: {
        description = "For investors who seek current income and stability and are less concerned about growth.";
        break;
      }
      case RiskToleranceType.ModeratelyConservative: {
        description = "For investors seeking current income and stability, with modest potential for increase in the value of their investments.";
        break;
      }
      case RiskToleranceType.Moderate: {
        description = `For long-term investors who don't need current income and want some growth potential.
          Likely to entail some fluctuations in value but presents less volatility than the overall equity market.`;
        break;
      }
      case RiskToleranceType.ModeratelyAggressive: {
        description = `For long-term investors who want good growth potential and don't need current income.
          Entails a fair amount of volatility but not as much as a portfolio invested exclusively in equities.`;
        break;
      }
      case RiskToleranceType.Aggressive: {
        description = `For long-term investors who want high growth potential and don't need current income.
          May entail substantial year-to-year volatility in value in exchange for potentially high long-term returns.`;
        break;
      }
      default:
          break;
    }

        return description;
  }

  get wizardStep (): DrivewealthWizardStep {
    return DrivewealthWizardStep.RiskToleranceQuestionnaire;
  }

  formatThemeColorString (input: string): string {
    return input.substring(1, input.length);
  }

  get riskPercentage (): number {
    const adjusted = ((this.riskToleranceQuestionnaireStore.score - 2.5) * 4);
    return adjusted / 100;
  }

}
